import "./PreviewRendererComponent.scss";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import TabLoaderComponent from "@/components/tab-loader/TabLoaderComponent.tsx";
import { useDispatch, useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import { setIframeUrl } from "@/store/actions/projectdata.action.ts";
import AppPreviewAndCodeStatusComponent
    from "@/components/common-components/app-preview-and-code-status/AppPreviewAndCodeStatusComponent.tsx";
import { useLocation, useParams } from "react-router-dom";
import { useSocket } from "@/services/hooks/useSocket";
import ErrorDisplayCard
    from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/app-tabs/app-code-and-preview/preview-renderer/error-display-card/ErrorDisplayCardComponent.tsx";
import { Button } from "@/components/ui/button.tsx";
import commonService from "@/services/common.service.ts";
import SandpackHtmlPreviewComponent
    from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/app-tabs/app-code-and-preview/preview-renderer/sandpack-html-preview/SandpackHtmlPreviewComponent.tsx";
import { AppType } from "@/models/project.model.ts";
import { CommonService } from "@/services";
import MemoizedIframeRenderer from "@/components/common-components/memoized-iframe-renderer/MemoizedIframeRendererComponent.tsx";


enum LoadingStatus {
    IDLE = "idle",
    LOADING = "loading",
    SUCCESS = "success",
    ERROR = "error",
    URL_ERROR = "url_error",
    PAUSED = "paused",
    ADVANCED_LOADING = "advanced_loading"
}

// Define the event types for preview events
enum PreviewEventType {
    PREVIEW_STARTED = "preview.started",
    PREVIEW_DELTA = "preview.delta",
    PREVIEW_SUCCESS = "preview.success",
    PREVIEW_ERROR = "preview.error"
}

const PreviewRendererComponent = () => {
    const dispatch = useDispatch();
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.IDLE);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [progressMessage, setProgressMessage] = useState<string>("");
    const iframeUrl = useSelector((state: IRootReducerState) => state.projectData.iframeUrl);
    const isEditMode = useSelector((state: IRootReducerState) => state.projectData.isEditMode);
    const isChatInProgress = useSelector((state: IRootReducerState) => state.projectData.isChatInProgress);
    const { projectId } = useParams<{ projectId: string }>();
    const location = useLocation();
    const appType: AppType = useMemo(() => commonService.getAppType(location.pathname), [location.pathname]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [catchyText, setCatchyText] = useState<string>("Good things take time...");

    const catchyTexts = useMemo(() => [
        "Good things take time...",
        "Brewing some code magic...",
        "Teaching the AI new tricks...",
        "Connecting the dots...",
        "Almost there, stay tuned...",
        "Crafting your masterpiece...",
        "Loading awesomeness...",
        "Patience is a virtue (and so is great code)..."
    ], []);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (loadingStatus === LoadingStatus.ADVANCED_LOADING) {
            interval = setInterval(() => {
                const randomIndex = Math.floor(Math.random() * catchyTexts.length);
                setCatchyText(catchyTexts[randomIndex]);
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [loadingStatus, catchyTexts]);

    const { events, clearEvent } = useSocket({
        eventTypes: [
            PreviewEventType.PREVIEW_STARTED,
            PreviewEventType.PREVIEW_DELTA,
            PreviewEventType.PREVIEW_SUCCESS,
            PreviewEventType.PREVIEW_ERROR
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'PreviewRenderer',
        debug: false, // Set to true only during development for detailed logs
    });


    const handleIframeError = useCallback(() => {
        setLoadingStatus(LoadingStatus.ERROR);
        commonService.handleMixpanelTrack("Preview generation error", {
            project_id: projectId,
            error: "Failed to load the preview. Please check your code for errors.",
            timestamp: new Date().toISOString()
        });
        setErrorMessage("Failed to load the preview. Please check your code for errors.");
    }, [projectId]);

    const handleIframePaused = useCallback(() => {
        setLoadingStatus(LoadingStatus.PAUSED);
        commonService.handleMixpanelTrack("App paused", {
            project_id: projectId,
            timestamp: new Date().toISOString()
        });
    }, [projectId]);

    const handleIframeTimeout = useCallback(() => {
        setLoadingStatus(LoadingStatus.PAUSED);
        commonService.handleMixpanelTrack("Preview timeout", {
            project_id: projectId,
            timestamp: new Date().toISOString()
        });
        setErrorMessage("The preview is taking longer than expected to load. The server might be waking up.");
    }, [projectId]);

    const handleRestartApp = useCallback(() => {
        setIsLoading(true);
        commonService._communications.reloadPreviewSubject.next("reload");
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);
    }, []);

    // Handle socket events - now they're pre-filtered by the hook
    useEffect(() => {
        if (events[PreviewEventType.PREVIEW_STARTED]) {
            CommonService._communications.setPreviewLoadingStateSubject.next(true);
            dispatch(setIframeUrl(""));
            setLoadingStatus(LoadingStatus.LOADING);
            setProgressMessage("Preview generation started");
            CommonService.handleMixpanelTrack("Preview generation started", {
                project_id: projectId,
                start_time: new Date().toISOString()
            });

            // Clear old errors when new preview starts
            CommonService._communications.clearPreviewErrorsSubject.next();
            clearEvent(PreviewEventType.PREVIEW_STARTED);
        }
        // Handle preview delta event (progress updates)
        if (events[PreviewEventType.PREVIEW_DELTA]) {
            setLoadingStatus(LoadingStatus.LOADING);
            const deltaEvent = events[PreviewEventType.PREVIEW_DELTA];
            const deltaMessage = deltaEvent?.event_meta?.msg
            setProgressMessage(deltaMessage);
            clearEvent(PreviewEventType.PREVIEW_DELTA);
        }

        // Handle preview success event
        if (events[PreviewEventType.PREVIEW_SUCCESS]) {
            CommonService._communications.setPreviewLoadingStateSubject.next(true);

            const successEvent = events[PreviewEventType.PREVIEW_SUCCESS];
            const url = successEvent?.event_meta?.preview_url;

            if (url) {
                CommonService.handleMixpanelTrack("Preview Generation Success", {
                    project_id: projectId,
                    preview_url: url,
                    timestamp: new Date().toISOString()
                });
                dispatch(setIframeUrl(url));
                clearEvent(PreviewEventType.PREVIEW_SUCCESS);
            }
        }
        if (events[PreviewEventType.PREVIEW_ERROR]) {
            const errorEvent = events[PreviewEventType.PREVIEW_ERROR];
            CommonService.handleMixpanelTrack("Preview Generation Error", {
                project_id: projectId,
                error: errorEvent?.error,
                timestamp: new Date().toISOString()
            });
            setLoadingStatus(LoadingStatus.ERROR);
            setErrorMessage(errorEvent?.error || "An error occurred while generating the preview.");
            clearEvent(PreviewEventType.PREVIEW_ERROR);
        }
    }, [events, dispatch, projectId, clearEvent]);
    // Render error state
    const renderErrorState = useCallback(() => (
        <AppPreviewAndCodeStatusComponent
            type="error"
            title="Error generating preview"
            description={errorMessage || "An error occurred while loading the preview."}
        >
            <div className="project-preview-error">
                <div className="mt-4">
                    <Button
                        onClick={handleRestartApp}
                        mixpanelEvent={{
                            name: "retry_preview_generation",
                            properties: {
                                project_id: projectId,
                                timestamp: new Date().toISOString()
                            }
                        }}
                        loading={isLoading}
                    >
                        Retry
                    </Button>
                </div>
            </div>
        </AppPreviewAndCodeStatusComponent>
    ), [errorMessage, isLoading, handleRestartApp, projectId]);

    // Render paused state
    const renderPausedState = useCallback(() => (
        <AppPreviewAndCodeStatusComponent
            type="info"
            title="Your app got paused"
            description="The app has been paused. Click the button below to restart it."
        >
            <div className="project-preview-paused">
                <div className="mt-4">
                    <Button
                        onClick={handleRestartApp}
                        mixpanelEvent={{
                            name: "restart_app",
                            properties: {
                                project_id: projectId,
                                timestamp: new Date().toISOString()
                            }
                        }}
                        loading={isLoading}
                    >
                        Restart App
                    </Button>
                </div>
            </div>
        </AppPreviewAndCodeStatusComponent>
    ), [projectId, isLoading, handleRestartApp]);

    const renderLoadingState = useCallback(() => {
        if (loadingStatus === LoadingStatus.ADVANCED_LOADING) {
            return (
                <AppPreviewAndCodeStatusComponent
                    type="loading"
                    title={catchyText}
                    description=""
                >
                    <div className="flex flex-col items-center justify-center mt-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                </AppPreviewAndCodeStatusComponent>
            );
        }
        if (appType === 'html') {
            return <SandpackHtmlPreviewComponent />
        } else {
            return <TabLoaderComponent progressMessage={progressMessage} />
        }
    }, [progressMessage, appType, loadingStatus, catchyText]);

    const handleIframeLoad = useCallback((iframeUrl: string, projectId: string, donotTriggerErrorCard?: boolean) => {
        if (iframeUrl) {
            setLoadingStatus(LoadingStatus.SUCCESS);
            if (!donotTriggerErrorCard) {
                CommonService._communications.triggerErrorCardSubject.next({
                    error: 'Iframe loaded - checking for errors',
                    timestamp: new Date().toISOString(),
                    projectId: projectId
                });
            }
        }
    }, []);

    useEffect(() => {
        if (iframeUrl) {
            setLoadingStatus(LoadingStatus.LOADING)
        }
    }, [iframeUrl])

    const renderStatuses = useCallback((loadingStatus: LoadingStatus) => {
        switch (loadingStatus) {
            case LoadingStatus.LOADING:
            case LoadingStatus.ADVANCED_LOADING:
                return renderLoadingState();
            case LoadingStatus.ERROR:
            case LoadingStatus.URL_ERROR:
                return renderErrorState();
            case LoadingStatus.PAUSED:
                return renderPausedState();
            case LoadingStatus.IDLE:
                return <AppPreviewAndCodeStatusComponent
                    type="info"
                    title="Apps Built by AI. Ideas Powered by You."
                    description="Build full web apps just by describing them. Our AI handles the code so you can focus on what matters—your ideas."
                />
        }
    }, [renderErrorState, renderLoadingState, renderPausedState])

    useEffect(() => {
        const sub = CommonService._communications.startCodeStreamerSubject.subscribe((value: boolean) => {
            if (value && appType === 'html') {
                setLoadingStatus(LoadingStatus.LOADING)
            }
        });
        return () => sub.unsubscribe();
    }, [appType]);

    useEffect(() => {
        const sub = CommonService._communications.setAdvancedModePreviewLoadingSubject.subscribe((value: boolean) => {
            if (value) {
                dispatch(setIframeUrl(""));
                setLoadingStatus(LoadingStatus.ADVANCED_LOADING)
            }
        });
        return () => sub.unsubscribe();
    }, [dispatch]);

    // Subscribe to preview loading state changes
    useEffect(() => {
        const sub = CommonService._communications.setPreviewLoadingStateSubject.subscribe((value: boolean) => {
            if (value) {
                setLoadingStatus(LoadingStatus.LOADING);
            }
        });
        return () => sub.unsubscribe();
    }, [dispatch]);


    // Determine if we should use Sandpack (for edit mode or live streaming) or iframe
    const shouldUseSandpack = useMemo(() => appType === 'html' && (isEditMode || isChatInProgress || !iframeUrl), [appType, isEditMode, isChatInProgress, iframeUrl]);

    if (!projectId) {
        return <></>;
    }

    return (
        <div className={`preview-renderer-container`}>
            {
                shouldUseSandpack ? <SandpackHtmlPreviewComponent /> :
                    iframeUrl ?
                        <div
                            className="iframe-wrapper"
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                                display: loadingStatus === LoadingStatus.SUCCESS ? 'block' : 'none'
                            }}
                        >
                            <MemoizedIframeRenderer
                                src={iframeUrl}
                                onLoad={(donotTriggerErrorCard?: boolean) => handleIframeLoad(iframeUrl, projectId, donotTriggerErrorCard)}
                                onError={handleIframeError.bind(null)}
                                onPaused={handleIframePaused.bind(null)}
                                onRestart={handleRestartApp.bind(null)}
                                onTimeout={handleIframeTimeout.bind(null)}
                                timeoutMs={10000}
                            />
                        </div>
                        : null
            }
            {/* Status overlays - only shown when iframe is not in SUCCESS state */}
            {loadingStatus !== LoadingStatus.SUCCESS && renderStatuses(loadingStatus)}
            <ErrorDisplayCard />

        </div>
    );
};
export default React.memo(PreviewRendererComponent);
